import Papa from 'papaparse';
import * as XLSX from 'xlsx';

export interface BOMRow {
  partNumber: string;
  description: string;
  manufacturer: string;
  quantity: number;
}

export const REQUIRED_HEADERS = ['partNumber', 'description', 'manufacturer', 'quantity'];
export const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
export const MAX_ROWS = 5000;

export function parseBOMCSV(csvText: string): { rows: BOMRow[]; errors: string[] } {
  const result = Papa.parse(csvText, { header: true, skipEmptyLines: true });
  const errors: string[] = [];
  const rows: BOMRow[] = [];

  // Validate headers
  const headers = result.meta.fields || [];
  for (const h of REQUIRED_HEADERS) {
    if (!headers.includes(h)) {
      errors.push(`Missing required header: ${h}`);
    }
  }

  // Validate rows
  for (const row of result.data as any[]) {
    if (!row.partNumber || !row.description || !row.manufacturer || !row.quantity) {
      errors.push(`Row missing required fields: ${JSON.stringify(row)}`);
      continue;
    }
    const quantity = parseInt(row.quantity, 10);
    if (isNaN(quantity) || quantity <= 0) {
      errors.push(`Invalid quantity for partNumber ${row.partNumber}`);
      continue;
    }
    rows.push({
      partNumber: row.partNumber.trim(),
      description: row.description.trim(),
      manufacturer: row.manufacturer.trim(),
      quantity,
    });
  }

  return { rows, errors };
}

export function parseBOMXLSX(buffer: ArrayBuffer): { rows: BOMRow[]; errors: string[] } {
  const errors: string[] = [];
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  const sheet = workbook.Sheets[sheetName];
  const json = XLSX.utils.sheet_to_json<any>(sheet, { defval: '' });
  const rows: BOMRow[] = [];

  // Validate headers
  const headers = Object.keys(json[0] || {});
  for (const h of REQUIRED_HEADERS) {
    if (!headers.includes(h)) {
      errors.push(`Missing required header: ${h}`);
    }
  }

  for (const row of json) {
    if (!row.partNumber || !row.description || !row.manufacturer || row.quantity === undefined || row.quantity === '') {
      errors.push(`Row missing required fields: ${JSON.stringify(row)}`);
      continue;
    }
    const quantity = parseInt(String(row.quantity), 10);
    if (isNaN(quantity) || quantity <= 0) {
      errors.push(`Invalid quantity for partNumber ${row.partNumber}`);
      continue;
    }
    rows.push({
      partNumber: String(row.partNumber).trim(),
      description: String(row.description).trim(),
      manufacturer: String(row.manufacturer).trim(),
      quantity,
    });
  }

  return { rows, errors };
}

export async function parseBOMFile(file: File, onProgress?: (p: number) => void): Promise<{ rows: BOMRow[]; errors: string[]; duplicates: string[] } > {
  const errors: string[] = [];
  const duplicates: string[] = [];

  if (file.size > MAX_FILE_SIZE_BYTES) {
    errors.push(`File too large. Maximum allowed size is ${MAX_FILE_SIZE_BYTES} bytes.`);
    return { rows: [], errors, duplicates };
  }

  const name = file.name.toLowerCase();
  if (name.endsWith('.csv')) {
    // Use Papa.parse on the File directly with chunk/worker so we can report progress
    return await new Promise((resolve) => {
      const rows: BOMRow[] = [];
      const seen = new Set<string>();
      const dupSet = new Set<string>();
      const parseErrors: string[] = [];
      let rowCount = 0;

      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        worker: true,
        chunk: (results: Papa.ParseResult<any>) => {
          const data = (results.data as any[]) || [];
          for (const row of data) {
            rowCount++;
            if (!row.partNumber || !row.description || !row.manufacturer || row.quantity === undefined || row.quantity === null || row.quantity === '') {
              parseErrors.push(`Row ${rowCount} missing required fields`);
              continue;
            }
            const quantity = parseInt(String(row.quantity), 10);
            if (isNaN(quantity) || quantity <= 0) {
              parseErrors.push(`Invalid quantity for partNumber ${row.partNumber} on row ${rowCount}`);
              continue;
            }
            const pn = String(row.partNumber).trim();
            if (seen.has(pn)) dupSet.add(pn);
            seen.add(pn);
            rows.push({
              partNumber: pn,
              description: String(row.description).trim(),
              manufacturer: String(row.manufacturer).trim(),
              quantity
            });
            if (rows.length > MAX_ROWS) {
              parseErrors.push(`Too many rows. Maximum allowed is ${MAX_ROWS}.`);
              // stop parser by returning (Papa provides no direct stop here; we rely on complete to finish)
            }
          }
          // progress: try to use meta.cursor if available
          try {
            const meta = (results as any).meta || {};
            const cursor = meta.cursor || 0;
            if (onProgress && file.size > 0) {
              const p = Math.min(1, cursor / file.size);
              onProgress(p);
            }
          } catch (e) {
            // ignore progress errors
          }
        },
        complete: () => {
          parseErrors.forEach(e => errors.push(e));
          const dups = Array.from(dupSet.values());
          resolve({ rows, errors, duplicates: dups });
        },
        error: (err: Error) => {
          errors.push(String(err));
          resolve({ rows: [], errors, duplicates: [] });
        }
      } as any);
    });
  } else if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
    const buffer = await file.arrayBuffer();
    const { rows, errors: xlsxErrors } = parseBOMXLSX(buffer);
    errors.push(...xlsxErrors);
    const seen = new Set<string>();
    for (const r of rows) {
      if (seen.has(r.partNumber)) duplicates.push(r.partNumber);
      seen.add(r.partNumber);
    }
    if (rows.length > MAX_ROWS) errors.push(`Too many rows. Maximum allowed is ${MAX_ROWS}.`);
    return { rows, errors, duplicates };
  } else {
    errors.push('Unsupported file type. Please upload CSV or XLSX.');
    return { rows: [], errors, duplicates };
  }
}
